/**
 * Feature flags · compile-time vertical gating.
 * When we expand beyond freight, add slugs here.
 */
export const ENABLED_VERTICALS = ["freight", "logistics"] as const;

/**
 * When false, broker signup/landing/dashboard flows are hidden. Flipped to
 * true on 2026-05-04 alongside the broker-free pricing tier launch — broker
 * lookup is now the core wedge, so the homepage variant toggle, founding-10
 * cohort copy, and signup CTAs all surface broker-side flows.
 */
export const BROKER_FLOWS_ENABLED = true;

/**
 * CAB ID issuance/claim program ("Claim your CAB #"). When false:
 *   - the dashboard claim flow (/dash/brokers/ca/claim), the claim-verify magic
 *     link, and the admin CA-broker claims queue render a "Coming soon" panel;
 *   - the "Claim your CAB #" broker nav entry is hidden;
 *   - the broker signup CA path shows "Coming soon" (US broker signup is
 *     unaffected); and
 *   - public marketing (the CAB Broker ID landing section + the "Get my CAB ID"
 *     CTAs) renders a "Coming soon" marker with the apply action disabled.
 *
 * Canadian broker LOOKUP / VERIFICATION is intentionally UNAFFECTED — it is a
 * separate, live surface, and existing CAB IDs still display in profiles and
 * lookups. Off until the CAB ID proof gate (BN9 + insurance COI + provincial
 * registration) lands, so an issued CAB ID means "verified", not just
 * "attested". Flip to true to re-enable the program.
 */
export const CAB_ID_ENABLED = false;

/**
 * Partner ELD devices ("record which ELD this partner runs" on My Partners).
 * When false the Devices action on partner cards is hidden entirely.
 *
 * ⚠ Arm together with the BACKEND flags, which this one does not replace:
 *   - system_flags.eld_lookup_enabled — the eld_device_lookup RPC the picker
 *     calls is platform-admin-only until that flips, so a customer with this
 *     UI flag on but that backend flag off gets an "admin only" search error;
 *   - system_flags.eld_revoked_notify — the alert this feature exists to feed
 *     (eld_revoked partner_watch_events) fires nothing until armed.
 * The carrier-to-device link is SELF-DECLARED by the customer; copy must
 * attribute it and never claim ELD compliance checking.
 */
export const ELD_PARTNER_DEVICES_ENABLED = false;

/**
 * Broker chameleon alert on BrokerDetailPage. OFF 2026-08-05 (founder ruling):
 * the detector was measured at 0 of 10 plausible flags — every match a
 * generic-suffix trigram collision (UBER FREIGHT LLC flagged against
 * "9 FREIGHT LLC"), the same-surety corroborator fired on 10/10, and nothing
 * requires the flagged broker to have registered AFTER the suspect died.
 * Component and tests stay; flip back only after the detector is rebuilt on
 * the succession-evidence model (see the carrier-side #912/#913 approach).
 * Measurement: memory project_broker_chameleon_measured_aug05.
 */
export const BROKER_CHAMELEON_ALERT_ENABLED = false;

/** When false, the Business Vertical selector is hidden (freight-only). Flip to true for multi-vertical support. */
export const MULTI_VERTICAL_ENABLED = false;

/**
 * When false, Alberta is removed from carrier-lookup province dropdowns and the
 * AB coverage entry is downgraded to "available" (manual, on request). Paused
 * 2026-05-22: Alberta's TRAVIS portal has a no-commercial-use clause in its
 * terms, so we no longer scrape it. Note: carrier-lookup-ca has no AB-specific
 * gate; AB is simply automated_supported:false there (an AB lookup with a DOT
 * still runs the cross-border FMCSA path). The paused piece is the standalone
 * ab-travis-monitor cron, gated by ENABLE_AB_TRAVIS_LOOKUPS on the backend.
 * This flag only governs what the UI offers. Flip back to true (and re-enable
 * ENABLE_AB_TRAVIS_LOOKUPS + wire the AB branch) to restore Alberta lookups.
 */
export const AB_LOOKUPS_ENABLED = false;

/**
 * No-card trial mode. When true, the billing surfaces (pricing CTAs, BillingPage)
 * grant a no-card 14-day trial via the set-plan edge function instead of starting
 * a Stripe checkout. Turned on 2026-06-17 because Stripe rejected the account
 * (rejected.fraud) and cannot collect payment.
 *
 * Mirrors the backend CR_TRIAL_MODE_OFF kill switch: when a real processor is
 * approved, set CR_TRIAL_MODE_OFF=true on the backend AND flip this to false (then
 * redeploy) so the UI reverts to paid checkout + the customer portal.
 * Turned off 2026-07-02: Paddle live approved, live checkout verified end-to-end.
 */
export const BILLING_TRIAL_MODE = false;

/**
 * Which payment processor the PAID billing path uses when BILLING_TRIAL_MODE is
 * false. Mirrors the backend BILLING_PROCESSOR switch.
 *   "stripe" — existing useStripeCheckout / customer-portal flow (today's behavior)
 *   "paddle" — usePaddleCheckout (Paddle.js overlay) + paddle-customer-portal
 *
 * Default "stripe" so turning trial mode off changes nothing until we explicitly
 * flip to "paddle" AFTER the Paddle account is approved and VITE_PADDLE_* env vars
 * (client token + per-tier price ids) are set. While this is "stripe" the Paddle
 * hooks (usePaddleCheckout / usePaddlePortal) are inert and unimported.
 */
export type BillingProcessor = "stripe" | "paddle";
export const BILLING_PROCESSOR: BillingProcessor =
  ((import.meta.env.VITE_BILLING_PROCESSOR as BillingProcessor | undefined) ?? "stripe");

/**
 * Multiple connected inboxes per user (currently one inbox per user per
 * provider — connecting a second one silently replaces the first, per
 * outlook-callback/gmail-callback's delete-then-insert). RULING (Jack
 * 2026-07-16): stub this flag now, default OFF, before reworking that
 * connect-callback model. Do NOT change the delete-then-insert replace
 * behavior until this is flipped true and the multi-inbox UI/backend
 * actually exists — the flag alone changes nothing.
 */
export const MULTI_INBOX_ENABLED = false;

/**
 * Hides the SMS delivery channel for partner watch alerts. Was a hardcoded
 * `false` until the CASL/CTIA consent flow (phone-verify-request/confirm,
 * vernot PR #375/#372) shipped 2026-07-10 — now env-driven so it can be
 * flipped without a redeploy, mirroring the VITE_BILLING_PROCESSOR precedent.
 * Leave VITE_PARTNER_ALERT_SMS unset (defaults to "false") until Twilio
 * delivery is wired AND live in carrier-monitor / partner-watch-diff — this
 * flag only gates the UI, not the backend sender.
 */
export const PARTNER_ALERT_SMS_ENABLED =
  ((import.meta.env.VITE_PARTNER_ALERT_SMS as string | undefined) ?? "false") === "true";

/**
 * The public "verify this report" share link — the "Copy verify link" action on
 * ShareReportButton, backed by the /verify/report page + the report-verify edge
 * function. Off for now: the public verification surface isn't ready to expose,
 * so report controls only offer "Download PDF" (rendered as a direct button, not
 * a one-item dropdown). Flip to true to restore the Copy-verify-link action
 * everywhere ShareReportButton renders (carrier/broker detail, My Trust Score,
 * and the package-scan result view).
 *
 * Re-flipped to false 2026-07-14 (founder): the verify-link flow isn't ready to
 * expose to customers yet; Download PDF stays for the report kinds that work.
 */
export const REPORT_VERIFY_LINK_ENABLED = false;

/**
 * Gmail as a connectable mailbox (Settings → Connect your email, and the
 * inbox empty-state CTA). Outlook has never been gated; this flag exists only
 * because Gmail was.
 *
 * WHY IT WAS OFF, AND WHY IT IS NOW ON (2026-08-23):
 * The whole Gmail backend has been live for months — `email-oauth-init` has a
 * Gmail branch, `gmail-callback` is deployed, the `gmail-inbox-sync` cron runs
 * every 15 minutes — but the button said "Gmail · soon" and prod had ZERO Gmail
 * accounts ever connected. The real blocker was outbound: vernot's
 * `_shared/send-email.ts` hardcoded `sendViaOutlook`, so a Gmail-only tenant
 * could finish OAuth and then have every sequence, outreach and POD-chase send
 * fail with "Outlook isn't connected" — a mailbox that looked linked and
 * silently sent nothing. That module now resolves the provider per send, and
 * `gmail-sync` gained the POD-chase reply/bounce last mile that only
 * `outlook-sync` had. With those landed the gate has nothing left to protect.
 *
 * ⚠ ONE OPERATIONAL PRECONDITION REMAINS, AND IT IS NOT CODE. Reading a
 * mailbox needs `gmail.readonly`, a Google RESTRICTED scope: until the Google
 * Cloud project finishes OAuth verification + a CASA Tier-2 assessment, the
 * consent screen shows an "unverified app" interstitial and the app is capped
 * at 100 users. Everything works below that cap. Set `VITE_GMAIL_CONNECT=false`
 * to hide the Gmail connect path again (e.g. if Google suspends the project)
 * without shipping a revert — the same env-driven kill-switch precedent as
 * PARTNER_ALERT_SMS_ENABLED above.
 *
 * Turning this OFF never strands an existing connection: already-linked Gmail
 * accounts keep syncing and sending, and a Reconnect button still renders for
 * an expired one. It only hides the FIRST connect.
 */
export const GMAIL_CONNECT_ENABLED =
  ((import.meta.env.VITE_GMAIL_CONNECT as string | undefined) ?? "true") === "true";

/**
 * Packet share links (2026-08-07) — the "Share packet" action on vault records
 * plus the public /share/report/:token recipient page. A share is a revocable,
 * TTL-bound capability minted by the report-share edge fn; DISTINCT from the
 * verify link above (that flag stays a separate founder call — this one does
 * not reopen it).
 *
 * ⚠ Arm together with the BACKEND flag, which this one does not replace:
 *   - system_flags.packet_share_enabled — report-share and report-share-view
 *     both return 404 while it is off, so this UI flag alone yields a Share
 *     dialog whose every call fails. Flip the backend flag first (emergency
 *     controls page), then this, then rendered-walk the recipient page states
 *     (dead code until now — CI green ≠ rendered walk).
 * Trial tenants cannot mint shares regardless (server 402 trial_download_locked:
 * a share link is sealed-record egress, same as the PDF download).
 */
/* ARMED 2026-08-08 (founder go). Backend `packet_share_enabled` must be ON
 * FIRST — with it off both edge functions 404 and this UI only offers a
 * dialog that says "not enabled yet". Flip order: backend flag, then merge
 * this, then publish, then walk the recipient states. */
export const PACKET_SHARE_ENABLED = true;

/**
 * Compliance Vault (formerly "Verification Records") — the customer-facing
 * evidence locker listing every sealed, signed verification record, with
 * re-download + independent verification. Ships FREE (adoption + retention
 * hook); the seam is here so long-retention / bulk-export can be gated to a
 * paid compliance tier later WITHOUT touching the page. Ungated today: every
 * tier with the scanner sees the vault. (Constant name kept for stability.)
 */
export const VERIFICATION_VAULT_ENABLED = true;

/**
 * Broker profile reviews (respond-to-reviews on the claimed broker profile).
 * The reviews feature has NOT shipped — only mock/demo UI exists — so the
 * pricing page hides the "Respond to reviews" bullet on the broker Free tier
 * while this is false. Profile CLAIM is live (registry ClaimFlow) and stays
 * advertised unconditionally. Flip to true when reviews ship.
 */
export const BROKER_REVIEWS_ENABLED = false;

/**
 * Carrier/broker onboarding-packet scanner (v1.5). When false, no scanner
 * route, button, or upload UI renders anywhere — the feature is invisible.
 *
 * What it does when on: a dispatcher uploads a partner's onboarding packet
 * (W9, MC authority letter, COI, voided check, signed agreement). The
 * scan-package edge function extracts the identity fields Haiku-first
 * (cheap), escalates to Sonnet only on low confidence, and compares the
 * packet identity against the authoritative FMCSA record (carriers_master /
 * broker_us_enrichment). A mismatch — an MC that belongs to a different legal
 * name, a different address or insurer — raises a fraud flag. This catches the
 * identity-theft pattern Bruno described (a scammer riding a real, clean
 * company's MC/DOT), which a public score structurally cannot see.
 *
 * Gating: migrated to the PhaseFeature system (key "package_scanner"), matching
 * package_builder. Platform admins bypass via PhaseGate.allowAdmin and tenants
 * drip-enable via companies.feature_overrides.package_scanner. The backend
 * (scan-package) gates on ENABLE_PACKAGE_SCANNER === "true" OR the caller being
 * a platform admin / override tenant.
 */

/**
 * Master gate for the self-serve REST API key flow. When false:
 *   - /dash/developers and /dash/developer hide the "Generate API key", key
 *     list, code samples that paste in YOUR_KEY, and webhook self-config UI;
 *     replaced by a single "Contact us for API integration & pricing" card.
 *   - /integrate (IntegrationGuidePage) collapses to the same contact CTA
 *     above the architecture diagram, hiding the four-step integration-key
 *     walkthrough.
 *   - Marketing "Developers" page swaps any "get a key" copy for "talk to us".
 *
 * Why off: as of 2026-05-21 we are NOT shipping self-serve REST API keys.
 * Pilot integrations (Bruno) are minted manually. Letting visitors mint a
 * `bc_int_…` or `cr_live_…` key with no sales touch creates support load
 * and a billing/rate-limit blast radius we are not ready for.
 *
 * Does NOT affect:
 *   - MCP server (/dash/developer/mcp). MCP agent keys are how we onboard
 *     pilot AI agents and are a separate surface from REST API keys.
 *   - Gateway certificate API. Public verify endpoints are gated by their
 *     own auth and exist at the worker layer, not the dashboard.
 *
 * Flip to true once: pricing locked, billing-tier provisioning works for
 * cold signups, and the docs are no longer marked "preview".
 */
export const SELF_SERVE_API_KEYS_ENABLED = false;

/**
 * Contact route surfaced when SELF_SERVE_API_KEYS_ENABLED is false.
 * Single source of truth so the four call-sites stay in sync.
 *
 * Positioning (2026-05-21): "Available on the Enterprise tier" frames the
 * REST API as a real product with a tier home (sets a price anchor and
 * filters out tire-kickers), while the CTA copy stays welcoming so any
 * team can start the conversation. The aim is "accessible but deliberate"
 * — we want to talk to interested teams, we just are not minting keys
 * without a scoping call yet.
 *
 * Routes to sales@ (2026-07-14): api@ is not a monitored inbox yet, so API +
 * pricing inquiries go to the same sales@cipherandrow.com used by the
 * Enterprise CTAs on Billing/Pricing, which is monitored.
 */
export const API_CONTACT_EMAIL = "sales@cipherandrow.com";
export const API_TIER_LABEL = "Enterprise tier";
export const API_CONTACT_CTA = "Available on the Enterprise tier — reach out to inquire";
export const API_CONTACT_SUBTITLE =
  "We're rolling out API access carefully and scoping each integration directly with the partner. Tell us about your use case, expected volume, and any endpoints you need — we're happy to talk with any team interested, no commitment up front.";

/**
 * Hard-hide for REST API surfaces. When false, the dashboard developer page,
 * the marketing integration guide, the docs page, and any nav link to them
 * are completely removed from the visible surface. Logged-in users hitting
 * /dash/developers or /dash/developer are redirected to /dash/developer/mcp
 * (MCP stays fully accessible — it is the active customer-facing channel).
 * Public visitors hitting /integrate or /docs are redirected to /.
 *
 * Distinct from SELF_SERVE_API_KEYS_ENABLED: that flag swaps "Generate a key"
 * UI for "Contact us" copy but still shows the page. This flag hides the
 * page entirely. Set both to false (current default) → API is invisible.
 * If only SELF_SERVE_API_KEYS_ENABLED flips on, you get the self-serve UX.
 * If only API_SURFACE_VISIBLE flips on, you get the Contact-us pages.
 *
 * Why hidden (2026-05-21): too early in pilot to surface any API messaging,
 * even gated. Re-enable once Bruno + 1-2 more pilots have shipped and we
 * have a documented integration path.
 */
export const API_SURFACE_VISIBLE = false;

export const FREIGHT_AGENT_TYPES = [
  "operations",
  "logistics",
  "broker",
  "carrier",
  "dispatcher",
  "freight_forwarder",
  "custom",
] as const;

export const FREIGHT_INDUSTRIES = [
  "Logistics",
  "Transportation",
  "Freight",
  "Supply Chain",
  "Warehousing",
  "3PL",
] as const;

export function isVerticalEnabled(slug: string): boolean {
  return (ENABLED_VERTICALS as readonly string[]).includes(slug);
}

// ── Feature flags for gated capabilities ──

export type FeatureFlag =
  | "fleet_dispatch"
  | "fleet_broadcast"
  | "sms_notifications"
  | "chrome_extension"
  | "embeddable_widget"
  | "inbound_webhooks"
  | "slack_notifications"
  | "broker_load_posting"
  | "broker_deal_engine"
  | "broker_carrier_search"
  | "rate_intelligence"
  | "double_broker_detect"
  | "load_board_aggregation"
  | "factoring_integration"
  | "hos_compliance"
  | "custom_agent_creation"
  | "agent_advanced_view";

type TierFlags = Record<FeatureFlag, boolean>;

/** All feature flags off — base for building a tier's flags with only the deltas. */
const ALL_FLAGS_OFF: TierFlags = {
  fleet_dispatch: false,
  fleet_broadcast: false,
  sms_notifications: false,
  chrome_extension: false,
  embeddable_widget: false,
  inbound_webhooks: false,
  slack_notifications: false,
  broker_load_posting: false,
  broker_deal_engine: false,
  broker_carrier_search: false,
  rate_intelligence: false,
  double_broker_detect: false,
  load_board_aggregation: false,
  factoring_integration: false,
  hos_compliance: false,
  custom_agent_creation: false,
  agent_advanced_view: false,
};

/** Build a tier's flags from the all-off base plus the enabled deltas. */
function tierFlags(overrides: Partial<TierFlags>): TierFlags {
  return { ...ALL_FLAGS_OFF, ...overrides };
}

/**
 * Tier → capability matrix.
 *
 * ⚠ THIS MAP IS KEYED BY TWO DIFFERENT VOCABULARIES AND MUST COVER BOTH.
 * `isFeatureEnabled` is called with a raw `companies.subscription_tier`
 * (FleetDispatchDialog, TierGate) *and* with a resolved PRICING_TIERS id
 * (`tier?.id` from usePlanUsage — NotificationSettingsPage, PartnerWatchToggle).
 * Those disagree: a dispatcher on the custom plan stores the bare string
 * "enterprise", which `getTierForCompany` resolves to the id
 * "dispatcher-enterprise". A missing key is NOT a loud failure — it falls
 * through to `free`, i.e. every capability silently OFF for a paying customer.
 * That is exactly how dispatcher Enterprise accounts were locked out of the SMS
 * partner-alert channel while the vernot `SMS_TIER_ALLOWLIST` was happily
 * allowing them server-side (found 2026-09-01).
 *
 * So: every id in PRICING_TIERS needs a row here, plus any legacy raw
 * subscription_tier string still reachable from the callers above.
 * `feature-flags.tier-coverage.test.ts` pins the PRICING_TIERS half.
 */
const TIER_FEATURES: Record<string, TierFlags> = {
  // Carrier Verified+ adds monitoring (gated elsewhere, not via these flags), so
  // it carries no extra FeatureFlag beyond Free. Present so trials resolve to a
  // real row instead of the Free fallback.
  "verified-plus": tierFlags({}),
  // Pre-plan dispatcher trial. Deliberately all-off — same as the Free
  // fallback it used to hit, so this row changes no behaviour; it exists so the
  // fallback is never the thing deciding, and so the vernot SMS_TIER_ALLOWLIST
  // (which likewise excludes "dispatcher-trial") has an explicit counterpart.
  "dispatcher-trial": tierFlags({}),
  // Broker paid tiers: present so a no-card trial of broker/broker-pro/scale
  // unlocks the carrier-search + fraud features the tier markets (previously
  // these fell back to Free = all off).
  broker: tierFlags({ broker_carrier_search: true, double_broker_detect: true }),
  "broker-pro": tierFlags({
    broker_carrier_search: true,
    double_broker_detect: true,
    rate_intelligence: true,
    sms_notifications: true,
    agent_advanced_view: true,
  }),
  "broker-unlimited": tierFlags({
    broker_carrier_search: true,
    double_broker_detect: true,
    rate_intelligence: true,
    sms_notifications: true,
    agent_advanced_view: true,
  }),
  free: {
    fleet_dispatch: false,
    fleet_broadcast: false,
    sms_notifications: false,
    chrome_extension: false,
    embeddable_widget: false,
    inbound_webhooks: false,
    slack_notifications: false,
    broker_load_posting: false,
    broker_deal_engine: false,
    broker_carrier_search: false,
    rate_intelligence: false,
    double_broker_detect: false,
    load_board_aggregation: false,
    factoring_integration: false,
    hos_compliance: false,
    custom_agent_creation: false,
    agent_advanced_view: false,
  },
  solo: {
    fleet_dispatch: false,
    fleet_broadcast: false,
    sms_notifications: false,
    chrome_extension: false,
    embeddable_widget: false,
    inbound_webhooks: false,
    slack_notifications: false,
    broker_load_posting: false,
    broker_deal_engine: false,
    broker_carrier_search: false,
    rate_intelligence: false,
    double_broker_detect: false,
    load_board_aggregation: false,
    factoring_integration: false,
    hos_compliance: false,
    custom_agent_creation: false,
    agent_advanced_view: false,
  },
  "broker-free": {
    fleet_dispatch: false,
    fleet_broadcast: false,
    sms_notifications: false,
    chrome_extension: false,
    embeddable_widget: false,
    inbound_webhooks: false,
    slack_notifications: false,
    broker_load_posting: false,
    broker_deal_engine: false,
    broker_carrier_search: true,
    rate_intelligence: false,
    double_broker_detect: false,
    load_board_aggregation: false,
    factoring_integration: false,
    hos_compliance: false,
    custom_agent_creation: false,
    agent_advanced_view: false,
  },
  pro: {
    fleet_dispatch: true,
    fleet_broadcast: false,
    sms_notifications: true,
    chrome_extension: false,
    embeddable_widget: false,
    inbound_webhooks: false,
    slack_notifications: false,
    broker_load_posting: false,
    broker_deal_engine: false,
    broker_carrier_search: false,
    rate_intelligence: true,
    double_broker_detect: true,
    load_board_aggregation: false,
    factoring_integration: false,
    hos_compliance: false,
    custom_agent_creation: false,
    agent_advanced_view: true,
  },
  // Dispatcher Scale: Pro's feature set with more seats/credits (capacity is
  // enforced by usage_limits, not these flags).
  "dispatcher-scale": {
    fleet_dispatch: true,
    fleet_broadcast: false,
    sms_notifications: true,
    chrome_extension: false,
    embeddable_widget: false,
    inbound_webhooks: false,
    slack_notifications: false,
    broker_load_posting: false,
    broker_deal_engine: false,
    broker_carrier_search: false,
    rate_intelligence: true,
    double_broker_detect: true,
    load_board_aggregation: false,
    factoring_integration: false,
    hos_compliance: false,
    custom_agent_creation: false,
    agent_advanced_view: true,
  },
  // Dispatcher Enterprise. `PRICING_TIERS` declares
  // `inheritsFrom: "dispatcher-scale"`, so it carries Scale's capability set
  // exactly — its extras (custom credit volume, SLA, dedicated AM) are
  // commercial terms, not FeatureFlags. Reached as the id
  // "dispatcher-enterprise" (usePlanUsage resolves the stored bare
  // "enterprise" through getTierForCompany's role-scoped custom-tier branch),
  // while the stored string itself lands on the `enterprise` row below.
  "dispatcher-enterprise": tierFlags({
    fleet_dispatch: true,
    sms_notifications: true,
    rate_intelligence: true,
    double_broker_detect: true,
    agent_advanced_view: true,
  }),
  // Legacy raw subscription_tier value only: LEGACY_TIER_ALIASES maps "fleet"
  // to "pro", so no resolved tier id is ever "fleet" — but FleetDispatchDialog
  // passes the raw column, so the key stays reachable. Zero prod companies
  // store it (vernot dropped it from SMS_TIER_ALLOWLIST for that reason).
  fleet: {
    fleet_dispatch: true,
    fleet_broadcast: true,
    sms_notifications: true,
    chrome_extension: false,
    embeddable_widget: false,
    inbound_webhooks: false,
    slack_notifications: false,
    broker_load_posting: false,
    broker_deal_engine: false,
    broker_carrier_search: false,
    rate_intelligence: true,
    double_broker_detect: true,
    load_board_aggregation: false,
    factoring_integration: false,
    hos_compliance: false,
    custom_agent_creation: false,
    agent_advanced_view: true,
  },
  enterprise: {
    fleet_dispatch: true,
    fleet_broadcast: true,
    sms_notifications: true,
    chrome_extension: true,
    embeddable_widget: true,
    inbound_webhooks: true,
    slack_notifications: true,
    broker_load_posting: true,
    broker_deal_engine: true,
    broker_carrier_search: true,
    rate_intelligence: true,
    double_broker_detect: true,
    load_board_aggregation: true,
    factoring_integration: true,
    hos_compliance: true,
    custom_agent_creation: true,
    agent_advanced_view: true,
  },
};

/**
 * Does this tier have its OWN row in the matrix, rather than silently falling
 * through to Free?
 *
 * `isFeatureEnabled` cannot answer this: an unknown tier and a tier whose row
 * is deliberately all-off (Verified+, dispatcher-trial) both read as "nothing
 * enabled". The distinction matters — the first is a bug that quietly walls a
 * paying customer, the second is intent. Used by
 * `feature-flags.tier-coverage.test.ts` to pin every PRICING_TIERS id.
 */
export function hasTierFeatureRow(tier: string | null | undefined): boolean {
  return tier != null && Object.prototype.hasOwnProperty.call(TIER_FEATURES, tier);
}

/**
 * Check if a feature is enabled for a given subscription tier.
 * Defaults to "free" tier if tier is unknown.
 */
export function isFeatureEnabled(tier: string | null | undefined, feature: FeatureFlag): boolean {
  const flags = TIER_FEATURES[tier ?? "free"] ?? TIER_FEATURES.free;
  return flags[feature] ?? false;
}

/**
 * Get all enabled features for a tier (useful for displaying feature lists).
 */
export function getEnabledFeatures(tier: string | null | undefined): FeatureFlag[] {
  const flags = TIER_FEATURES[tier ?? "free"] ?? TIER_FEATURES.free;
  return (Object.entries(flags) as [FeatureFlag, boolean][])
    .filter(([, enabled]) => enabled)
    .map(([flag]) => flag);
}

// ── Phase gating (Phase 1 rollout, Bruno pilot → June GA) ──
//
// Orthogonal to TIER_FEATURES above: tiers control what a paying customer unlocks,
// phases control what has been *productized* yet. A Phase 1 enterprise customer
// still cannot see Phase 2 features · they ship together when we flip the mode.

export type Phase = "phase1" | "phase2";

/** Feature keys that are Phase 2 only. Everything else is always available. */
export type PhaseFeature =
  | "load_board"
  | "load_posting"
  | "dispatch_hub"
  | "fleet_registry"
  | "deal_engine"
  | "trust_links"
  | "rate_intelligence"
  | "marketplace"
  | "deal_builder"
  | "batch_documents"
  | "drivers"
  | "procurement"
  | "chrome_extension"
  | "website_scanner"
  | "email_ai"
  | "automation_rules"
  // AI email observer review page (read-only "what it would have sent" + affirm).
  | "email_ai_observer"
  | "broker_lookup"
  | "document_ai_batch"
  | "email_templates"
  // The POD chase sequence + its featured card and "Start POD chase" flow.
  // Held OFF in ALL phases via UNRELEASED_FEATURES: the POD upload workflow
  // isn't ready and it overcomplicates a dispatcher's existing process, so the
  // first customers get plain follow-up sequences instead. Sequences themselves
  // stay on — this hides ONLY the POD chase. Keep exercising it on the test
  // tenant via companies.feature_overrides.pod_chase = true (AdminHub).
  | "pod_chase"
  | "invoices"
  | "documents"
  | "fleet_tracking"
  | "carrier_vetting_swipe"
  | "embed_badge"
  | "package_builder"
  | "package_scanner"
  // Heavy UI/UX rebuild of the package scanner. Held OFF in ALL phases via
  // UNRELEASED_FEATURES so the GA'd v1 scanner keeps rendering for everyone;
  // dogfood per-tenant via companies.feature_overrides.package_scanner_v2 = true
  // (AdminHubPage). Same scan-package backend as v1 — this only swaps the UI,
  // so the flip is frontend-only and fully reversible. Promote it to GA by
  // removing it from UNRELEASED_FEATURES (and clearing any per-tenant overrides).
  | "package_scanner_v2"
  // v3 scanner extras (the CR sign + seal / SealVerifyCard in the scan results).
  // Held OFF in ALL phases via UNRELEASED_FEATURES — it was rendering for everyone
  // because the key was never registered, so isPhaseFeatureEnabled fell through to
  // "on". Dogfood per-tenant via companies.feature_overrides.package_scanner_v3 = true.
  | "package_scanner_v3"
  // The scanner results "Build a CR-verified package" hand-off CTA. Held OFF via
  // UNRELEASED_FEATURES (founder request — the review/confirm flow ships without
  // it); dogfood per-tenant via companies.feature_overrides.package_scanner_build = true.
  | "package_scanner_build"
  // Self-serve trust-bus webhook subscriptions on the Developers page. Held OFF
  // in ALL phases via UNRELEASED_FEATURES and named to MATCH the backend gate
  // exactly (companies.feature_overrides.trust_bus_api), which vernot's
  // webhook-subscribe and generate-webhook-secret both enforce fail-closed. One
  // key flips the UI and the API together; a UI-only key would show a panel
  // whose every write the backend rejects with 403.
  | "trust_bus_api"
  // Free-form reviewer notes on a scan (Activity tab). The vernot scan-notes
  // backend SHIPPED 2026-07-07 (scan-notes edge fn + migration
  // 20260706180000_scan_notes) and is live/active in prod. This flag stays in
  // UNRELEASED_FEATURES as a deliberate per-tenant rollout gate, not because
  // the backend is missing: it now resolves as an explicit override OR'd with
  // package_scanner_v2, so any tenant on the v2 scanner gets the Notes tab
  // without a separate flip (see ResultsPanel.tsx:267-273). Dogfood/override
  // per-tenant via companies.feature_overrides.package_scanner_notes = true.
  | "package_scanner_notes"
  // The scanner's public secure-share link (ExportDestinationsModal "Secure share
  // link" tile). Held OFF via UNRELEASED_FEATURES; dogfood per-tenant via
  // companies.feature_overrides.scanner_share_link = true.
  | "scanner_share_link"
  // CR Verified ownership-verification (badge + My Trust Score verify wizard).
  // Held OFF in ALL phases via UNRELEASED_FEATURES until the ownership backend is
  // enabled; admins dogfood via companies.feature_overrides.cr_verified = true.
  | "cr_verified"
  // Score-dispute intake (credit-bureau style first-line of defense for
  // entities — brokers first — contesting their trust score, so upstream data
  // providers never absorb raw complaint volume). Backend: score-dispute edge
  // function + score_disputes table in vernot, gated by ENABLE_SCORE_DISPUTES.
  // The intake UI now exists at /dash/disputes/scores (PhaseGate-wrapped route
  // + admin queue tab in Trust & Safety). Still held OFF in ALL phases via
  // UNRELEASED_FEATURES — release stays a manual decision; admins reach it via
  // PhaseGate allowAdmin and tenants dogfood via
  // companies.feature_overrides.score_disputes = true.
  | "score_disputes"
  // Trust Signature (sign-anchor / document-signing seal) settings surface.
  // Phase 2 only — hidden in phase1, surfaced in phase2 or via a per-tenant
  // companies.feature_overrides.trust_signature = true override (dogfood).
  | "trust_signature"
  // Admin-only Paddle checkout gate. Held OFF in ALL phases via
  // UNRELEASED_FEATURES so the no-card trial keeps rendering for everyone;
  // dogfood per-tenant via companies.feature_overrides.paddle_checkout = true
  // (AdminHub -> Tenant detail). When ON, BillingPage exits trial mode and uses
  // the Paddle checkout for that tenant only. Frontend-only gate; the Paddle
  // overlay also needs VITE_PADDLE_CLIENT_TOKEN + VITE_PADDLE_PRICE_* set.
  | "paddle_checkout"
  // Credit packs (prepaid top-up credits) storefront. GA'd 2026-07-12 (founder:
  // packs live in the dash billing page for subscribed accounts only, never on
  // the public pricing page). The card renders only for eligible org admins on
  // an active paid finite-credit tier, and the backend separately requires
  // system_flags.credit_packs_enabled (purchase) and credit_packs_spend_enabled
  // (spend fall-through) plus the Paddle pack catalog, so those flags remain
  // the master kill switches. Eligibility is enforced server-side in
  // create-paddle-checkout regardless of this flag.
  | "credit_packs"
  // My Trust Score page (/dash/carriers/my-score). Pulled from GA 2026-07-14
  // (founder): the page ships only a bare number today — no breakdown, no fraud
  // signals, no raise-your-score guidance — and its report PDF 404s, so it
  // undercuts the first impression for the exact audience it exists for. Held
  // in UNRELEASED_FEATURES while the full-breakdown rebuild lands; admins
  // dogfood via PhaseGate allowAdmin and tenants drip-enable via
  // companies.feature_overrides.my_trust_score = true.
  | "my_trust_score"
  // Self-serve Document Scan API — the settings surface (/dash/settings/scan-api)
  // where a tenant mints scan-only API keys and copies the three-call quickstart
  // (scan-api-upload → PUT bytes → scan-api-submit → scan-api-result). Keys minted
  // here carry permissions:['scan'] ONLY; this surface never exposes the deal
  // engine, agent registration, load posting, or the general REST API. Held OFF
  // in ALL phases via UNRELEASED_FEATURES; admins dogfood via PhaseGate allowAdmin
  // and tenants drip-enable via companies.feature_overrides.scan_api = true.
  | "scan_api"
  // Scan agent layer — the human side of the API/agent escalation rail: the
  // "Needs review (agent-flagged)" queue (/dash/packages/review-queue) listing
  // scans an agent escalated via scan-api-request-review / cr_scan_request_review.
  // Separate flag from scan_api so the agent layer can stay dark even where the
  // base API is on (matches the backend's feature_overrides.scan_agent_layer
  // gate in vernot _shared/api-key-auth.ts). Held OFF in ALL phases via
  // UNRELEASED_FEATURES; per-tenant override is the only arming path.
  | "scan_agent_layer"
  // Standalone simulated Packet Scanner demo (/demo/scanner): the full upload →
  // scan → review → export flow replayed on fictional data, fully client-side
  // (no scan-package, no credits, no persistence). The /demo/scanner route
  // went PUBLIC 2026-07-26 (founder flip; landing-nav CTA) — this flag now
  // only gates the dash sidebar's "Scanner Demo" entry (admins + tenants via
  // companies.feature_overrides.scanner_demo), so tenant dashboards aren't
  // cluttered with a demo link they don't need.
  | "scanner_demo"
  // Broker-side "Verify a COI from this carrier" card on /dash/carriers/:id.
  // Held OFF in ALL phases via UNRELEASED_FEATURES (founder 2026-08-09: the
  // flow is wired end to end — coi-upload → coi-upload-verify → document-extract
  // + validate-insurance, all deployed — but it has not been proven against a
  // real certificate, so it stays dark rather than render an unproven verdict
  // on the surface brokers vet from). Dogfood per-tenant via
  // companies.feature_overrides.coi_verify = true (AdminHub → Tenant detail).
  //
  // ⚠ FRONTEND-ONLY gate. coi-upload / coi-upload-verify are JWT-authed but
  // carry no feature check, so this hides the card without closing the door.
  // If the flow needs to be genuinely unreachable, add the matching
  // feature_overrides.coi_verify gate in vernot's coi-upload first.
  | "coi_verify";

// 2026-04-21 scope lock for Bruno pilot: verification-only wedge.
// Phase 1 CORE = carrier lookup (US + CA), broker lookup, public profile pages
// + claim flow, settings, home. EVERYTHING ELSE hidden until Bruno signs off,
// then drip-release per-tenant via companies.feature_overrides.
// broker_lookup is NOT in the hide set — it's the core demo.
const PHASE2_FEATURES = new Set<PhaseFeature>([
  "load_board",
  "load_posting",
  "dispatch_hub",
  "deal_engine",
  "rate_intelligence",
  "marketplace",
  "deal_builder",
  "procurement",
  "chrome_extension",
  "website_scanner",
  "email_ai",
  "automation_rules",
  "document_ai_batch",
  "trust_links",
  "batch_documents",
  "drivers",
  "fleet_registry",
  "email_templates",
  "invoices",
  "documents",
  "fleet_tracking",
  // carrier_vetting_swipe re-flagged 2026-05-05 to hide the swipe surface from
  // tenants by default. The original blank-page concern (no override → blank deck)
  // is resolved: CarrierVettingSwipePage now redirects to /dash/carriers/lookup
  // when the flag resolves false, and the SwipeViewToggle in the page only
  // renders when the flag is on. Admins still bypass via PhaseGate.allowAdmin.
  "carrier_vetting_swipe",
  // Embed-your-trust-badge surface on MyTrustScorePage. Backend route
  // `/v1/embed.js` (trust-score widget) shipped 2026-05-04 but no UI for
  // carriers to discover or copy the snippet. Flagged off so admins can
  // dogfood + verify before public exposure. Admin override via
  // AdminHubPage / companies.feature_overrides.embed_badge = true.
  "embed_badge",
  // Branded onboarding-package builder (wizard → CR-stamped PDF). Backend
  // build-package edge fn ships gated by ENABLE_PACKAGE_BUILDER; this hides the
  // UI by default. Drip-enable per tenant via feature_overrides.package_builder.
  "package_builder",
  // package_scanner GA'd 2026-06-13: removed from PHASE2 so all logged-in roles
  // see it. The free-carrier "free carrier verifications" exploit is closed
  // server-side, not by this flag: scan-package detects the packet's true
  // entity type and a carrier-role caller cannot carrier-verify via a scan
  // (broker packets stay free for carriers). Backend still requires
  // ENABLE_PACKAGE_SCANNER=true.
  // Also held by UNRELEASED_FEATURES below, so it stays dark even in phase2.
  "cr_verified",
  // Score-dispute intake. Also held by UNRELEASED_FEATURES until the UI ships.
  "score_disputes",
  // Trust Signature (sign-anchor) settings surface — Phase 2 only.
  "trust_signature",
]);

/**
 * Features NOT YET RELEASED in ANY phase. isPhaseFeatureEnabled returns false for
 * these regardless of phase (phase2 would otherwise enable every flag), so they
 * stay dark on prod. The only way to surface one is an explicit per-tenant
 * override (companies.feature_overrides.<key> = true) for dogfooding, because
 * resolveFeature checks the override BEFORE the phase logic. Remove a key to
 * release it. cr_verified stays here until the ownership-verification backend is
 * enabled (ENABLE_OWNERSHIP_VERIFICATION), so the checkmark never appears before
 * ownership can actually be proven.
 */
const UNRELEASED_FEATURES = new Set<PhaseFeature>([
  "cr_verified",
  "score_disputes",
  // AI email observer review page. Dark everywhere until the internal founder-inbox
  // proof clears; dogfood per-tenant via companies.feature_overrides.email_ai_observer
  // (pair it with the backend companies.feature_overrides.email_ai_shadow_observe flag,
  // which is what actually collects the observations).
  "email_ai_observer",
  // package_scanner_v2 GA'd 2026-07 (entry deleted per the flag's own promote
  // recipe): v2 is now the default scanner UI for every tenant; per-tenant
  // opt-out stays available via feature_overrides.package_scanner_v2 = false.
  // Off everywhere until the v3 sign + seal is ready; dogfood per-tenant via
  // feature_overrides.package_scanner_v3. Without this, ResultsPanel's
  // usePhaseFeature("package_scanner_v3") resolved to ON for everyone.
  "package_scanner_v3",
  // The scanner results "Build a CR-verified package" CTA — off everywhere until
  // the build hand-off is ready; dogfood per-tenant via feature_overrides.package_scanner_build.
  "package_scanner_build",
  // Trust-bus webhook subscriptions. Registering the key here is LOAD-BEARING:
  // isPhaseFeatureEnabled falls through to ON for any key it does not know, so
  // an unregistered flag renders for everyone — exactly how package_scanner_v3
  // shipped visible. Dogfood per-tenant via feature_overrides.trust_bus_api,
  // which simultaneously opens the vernot doors this panel calls.
  "trust_bus_api",
  // Reviewer notes on a scan — the vernot scan-notes backend is LIVE (shipped
  // 2026-07-07); this membership is a deliberate per-tenant rollout gate (OR'd
  // with package_scanner_v2 in ResultsPanel), not a stand-in for a missing
  // backend. Dogfood/override per-tenant via feature_overrides.package_scanner_notes.
  "package_scanner_notes",
  // The scanner's public secure-share link — held off until it's ready.
  "scanner_share_link",
  // POD chase — off everywhere while the POD workflow isn't ready. Sequences
  // are unaffected; this hides the POD chase option, its featured card and the
  // "Start POD chase" flow. Dogfood per-tenant via feature_overrides.pod_chase.
  "pod_chase",
  // Admin-only Paddle checkout dogfood gate — off for everyone until a tenant
  // override flips it (AdminHub). Keeps the no-card trial live for all users.
  "paddle_checkout",
  // My Trust Score — dark everywhere until the full-breakdown rebuild ships
  // (founder 07-14). Route + nav + home-page CTAs all gate on this key.
  "my_trust_score",
  // Self-serve Document Scan API settings surface — dark everywhere until it's
  // ready to expose; dogfood per-tenant via feature_overrides.scan_api = true.
  "scan_api",
  // Agent-flagged review queue — dark everywhere; per-tenant
  // feature_overrides.scan_agent_layer is the only arming path.
  "scan_agent_layer",
  // Simulated scanner demo — the PUBLIC route is ungated (2026-07-26); this
  // membership only keeps the dash sidebar entry admin/override-gated.
  "scanner_demo",
  // Broker-side COI verify card. Registering the key here is LOAD-BEARING:
  // isPhaseFeatureEnabled falls through to ON for any key it does not know, so
  // dropping this line renders the card for every tenant (exactly how
  // package_scanner_v3 shipped visible). Dogfood via feature_overrides.coi_verify.
  "coi_verify",
]);

/**
 * Phase features that actually have a real route + PhaseGate today (a GA-ready
 * surface a tenant can reach once the override is flipped on). Single source of
 * truth so the AdminHub toggle UI and the gate definitions cannot drift: every
 * key NOT in this set is a scaffold (declared for the override type, but with no
 * consuming route or nav item yet — flipping it on does nothing visible).
 *
 * Keep in sync with App.tsx routes wrapped in <PhaseGate feature="..."> and the
 * dash-v2 nav. Adding a route for a scaffold key? Move its key in here too.
 *
 * Does NOT change any default OFF state — PHASE2_FEATURES still hides all of
 * these in phase1; this only tells the admin UI which toggles are wired.
 */
export const WIRED_PHASE_FEATURES = new Set<PhaseFeature>([
  "load_board",
  "email_ai",
  "automation_rules",
  // Observer has a real route (/dash/email/observer, PhaseGate-wrapped) + page.
  "email_ai_observer",
  "email_templates",
  "documents",
  "trust_links",
  "embed_badge",
  "fleet_tracking",
  "invoices",
  "broker_lookup",
  "carrier_vetting_swipe",
  "package_builder",
  "package_scanner",
  // Wired: PackageScannerPage swaps to the v2 shell when this resolves true, so
  // the AdminHub toggle does something visible (not a scaffold-only key).
  "package_scanner_v2",
  // SealVerifyCard mounts in ResultsPanel when this resolves true, so the
  // AdminHub toggle is wired (not a scaffold-only key).
  "package_scanner_v3",
  // The scanner export's secure-share-link tile is gated on this.
  "scanner_share_link",
  "cr_verified",
  "score_disputes",
  // Trust Signature has a real route + PhaseGate (settings/sign-anchor).
  "trust_signature",
  // Wired: BillingPage exits trial mode + uses the Paddle checkout when on.
  "paddle_checkout",
  // Wired: BillingPage renders the Buy Credits card + the usage meters show the
  // purchased-credit balance when on.
  "credit_packs",
  // Wired: /dash/carriers/my-score route is PhaseGate-wrapped + nav-gated.
  "my_trust_score",
  // Wired: /dash/settings/scan-api route is PhaseGate-wrapped + the "Scan API"
  // tab in SettingsShell gates on this key, so the AdminHub toggle is visible.
  "scan_api",
  // Wired: /dash/packages/review-queue route is PhaseGate-wrapped + nav-gated.
  "scan_agent_layer",
  // Wired: the dash sidebar "Scanner Demo" nav item gates on this key (the
  // public /demo/scanner route itself is ungated since 2026-07-26).
  "scanner_demo",
  // Wired: CarrierCoiVerifyCard mounts on /dash/carriers/:id only when this
  // resolves true, so the AdminHub toggle does something visible.
  "coi_verify",
]);

/**
 * True when the given phase feature has a real route + gate today (vs a
 * scaffold-only key with no GA surface). Drives the AdminHub "Not yet wired"
 * affordance. Does not affect gating — see isPhaseFeatureEnabled for that.
 */
export function isPhaseFeatureWired(feature: PhaseFeature): boolean {
  return WIRED_PHASE_FEATURES.has(feature);
}

/**
 * Is a feature available in the given phase?
 * Phase 1 only surfaces verification + email + settings. Phase 2 opens everything else.
 *
 * DRIP-RELEASE WARNING (2026-05-02 hivesim red team finding):
 * Do NOT flip a tenant's `companies.settings.dashboard_mode` from `"audit"` to
 * something else to unlock features — that wholesale-unlocks every PHASE2_FEATURES
 * entry at once and blasts Bruno (or any pilot) with 22 half-built surfaces in
 * a single session. Instead, drip features per-tenant via
 * `companies.feature_overrides.<feature_key> = true` so each surface unlocks
 * deliberately. AdminHubPage.writeFeatureOverride is the supported path.
 */
export function isPhaseFeatureEnabled(phase: Phase, feature: PhaseFeature): boolean {
  // Unreleased features stay dark in every phase (phase2 would otherwise enable
  // all flags). Only an explicit per-tenant override surfaces them.
  if (UNRELEASED_FEATURES.has(feature)) return false;
  if (phase === "phase2") return true;
  return !PHASE2_FEATURES.has(feature);
}

// ── Dash v2 rebuild flags (added 2026-04-10) ──
//
// Ground-up dashboard rebuild from Paper artboards. While v2 is being built, the
// old /dashboard routes all collapse to an Under Reconstruction page. The new
// dash lives under /dash until v2 is proven, then swaps to /dashboard.

/**
 * Master kill switch for the v2 dashboard. Defaults to false so the old dash
 * (now an Under Reconstruction page) keeps rendering for anyone not opted in.
 * Flip to true per-tenant via companies.feature_overrides, or globally once v2
 * is proven.
 */
export const DASH_V2_DEFAULT = false;

/**
 * Controls whether carriers can sign up without an invitation.
 * - "public":     anyone can sign up at /carriers/signup
 * - "invite_only": signup form replaced with "contact your dispatcher" message,
 *                  only trust-link invite tokens can create a carrier account
 *
 * Global default is "public". Tenants can override via admin hub (writes to
 * companies.feature_overrides.carrierSignupMode). The check is enforced server-side
 * in external-agent-register, NOT just frontend.
 */
export type CarrierSignupMode = "public" | "invite_only";
export const CARRIER_SIGNUP_MODE_DEFAULT: CarrierSignupMode = "public";

/**
 * Check if a tenant has dash v2 enabled. Reads from the company's feature_overrides
 * JSONB column (set via tenant-config edge function), falls back to global default.
 */
export function isDashV2Enabled(featureOverrides: Record<string, unknown> | null | undefined): boolean {
  if (featureOverrides && typeof featureOverrides.dashV2 === "boolean") {
    return featureOverrides.dashV2;
  }
  return DASH_V2_DEFAULT;
}

/**
 * Resolve carrier signup mode for a tenant (or global if no tenant context).
 */
export function resolveCarrierSignupMode(
  featureOverrides: Record<string, unknown> | null | undefined,
): CarrierSignupMode {
  const override = featureOverrides?.carrierSignupMode;
  if (override === "public" || override === "invite_only") {
    return override;
  }
  return CARRIER_SIGNUP_MODE_DEFAULT;
}

// ── Dashboard tour gating (added 2026-05-24) ──
//
// The post-signup dashboard tour (src/components/dash-v2/DashboardTour.tsx)
// auto-launches a driver.js walkthrough on a user's first /dash visit, keyed by
// logistics role. Removed from the default experience 2026-05-24: all three
// roles are OFF, so new users no longer get the auto-prompt and the Settings
// "Retake dashboard tour" button is hidden. The tour component, its anchors, and
// the tour_completed plumbing are all left intact — flip a role back to true
// (and redeploy) to restore that role's tour with no other wiring.

export type TourRole = "dispatcher" | "broker" | "carrier";

export const DASHBOARD_TOUR_ENABLED: Record<TourRole, boolean> = {
  dispatcher: false,
  broker: false,
  carrier: false,
};

/**
 * Is the post-signup dashboard tour enabled for a given role? An unknown/null
 * role resolves to false (no tour) — matches the off-by-default stance and means
 * role-less or non-owner accounts never get an orphaned tour.
 */
export function isDashboardTourEnabled(role: string | null | undefined): boolean {
  if (!role) return false;
  return DASHBOARD_TOUR_ENABLED[role as TourRole] ?? false;
}

/**
 * True if any role's tour is enabled. Lets role-agnostic call-sites (e.g. the
 * layout's onboarding-banner timing) skip tour-completion waits entirely when no
 * tour can ever run.
 */
export function isAnyDashboardTourEnabled(): boolean {
  return Object.values(DASHBOARD_TOUR_ENABLED).some(Boolean);
}

// ── CR Registry — public registry flags (added 2026-05-02) ──
//
// Phase 2 Wave 2 work. Default OFF in production (so the new public registry
// surface is invisible until we explicitly cut it over) and ON in development
// builds (`import.meta.env.MODE === 'development'`) so local builds always
// see the new pages without per-developer overrides. Each flag below maps to
// a specific surface so we can drip-release independently:
//
//   - public_registry      — /registry home + search results page
//   - registry_claim_flow  — claim-this-listing flow on detail pages
//   - registry_mcp_tools   — MCP tools that read the public registry
//   - registry_seo_pages   — auto-generated SEO landing pages per identifier
//
// All four are cohesive: ship the home + claim + MCP + SEO together when GA.

export type RegistryFeatureFlag =
  | "public_registry"
  | "registry_claim_flow"
  | "registry_mcp_tools"
  | "registry_seo_pages";

const REGISTRY_FLAG_DEFAULTS_DEV: Record<RegistryFeatureFlag, boolean> = {
  public_registry: true,
  registry_claim_flow: true,
  registry_mcp_tools: true,
  registry_seo_pages: true,
};

const REGISTRY_FLAG_DEFAULTS_PROD: Record<RegistryFeatureFlag, boolean> = {
  public_registry: true,
  registry_claim_flow: true,
  registry_mcp_tools: true,
  registry_seo_pages: true,
};

/**
 * Read the current value of a registry feature flag.
 * Defaults vary by build mode: ON in dev, OFF in prod. We read
 * `import.meta.env.MODE` (Vite) the same way `src/lib/sentry.ts` does.
 */
export function isRegistryFeatureEnabled(flag: RegistryFeatureFlag): boolean {
  const isDev = import.meta.env.MODE === "development";
  const defaults = isDev ? REGISTRY_FLAG_DEFAULTS_DEV : REGISTRY_FLAG_DEFAULTS_PROD;
  return defaults[flag] ?? false;
}

/** Convenience accessor used by route gates and feature surfaces. */
export const registryFeatures = {
  get public_registry() {
    return isRegistryFeatureEnabled("public_registry");
  },
  get registry_claim_flow() {
    return isRegistryFeatureEnabled("registry_claim_flow");
  },
  get registry_mcp_tools() {
    return isRegistryFeatureEnabled("registry_mcp_tools");
  },
  get registry_seo_pages() {
    return isRegistryFeatureEnabled("registry_seo_pages");
  },
} as const;
